import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  type ColumnDef,
  type ColumnFiltersState,
  type OnChangeFn,
  type PaginationState,
  type SortingState,
  flexRender,
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  useReactTable,
} from '@tanstack/react-table'
import { useTranslation } from 'react-i18next'
import {
  type PackagingSupplier,
  type PackagingSuppliersOverviewSortBy,
  type PackagingSuppliersOverviewSortDir,
  downloadPackagingSuppliersExport,
  useListPackagingSuppliers,
} from '@/shared/hooks/api/packagingSuppliers'
import { useGetUserSession } from '@/shared/hooks/api/auth'
import { useCurrentOrganization } from '@/shared/hooks/api/organization'
import { cn } from '@/shared/lib/utils'
import {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { DataTableColumnHeader, DataTablePagination, DataTableToolbar } from '@/components/data-table'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { PackagingSupplierFunnelStatusCell } from './PackagingSupplierFunnelStatusCell'
import { PackagingSupplierNameCell } from './PackagingSupplierNameCell'
import { PackagingSupplierRedFlagsCell } from './PackagingSupplierRedFlagsCell'
import {
  PACKAGING_SUPPLIER_FUNNEL_STAGE_VALUES,
  PACKAGING_FIELD_CROSS_CHECK_ISSUE_CODE_VALUES,
  funnelStageLabelKey,
  auditIssueLabelKey,
} from './packagingSupplierOverviewColumns'
import { derivePackagingSupplierFunnelStage } from './packagingSupplierStats'
import { ExportXlsxButton } from '@/features/packaging-suppliers/export/ExportXlsxButton'
import { buildPackagingExportFilename } from '@/features/packaging-suppliers/export/packagingExportFilename'
import {
  type SuppliersListState,
  saveSuppliersListState,
} from './suppliersListNavigation'

const suppliersTableColumns = [
  { width: '28%' },
  { width: '10%' },
  { width: '38%' },
  { width: '24%' },
] as const

function SuppliersTableColGroup() {
  return (
    <colgroup>
      {suppliersTableColumns.map((column, index) => (
        <col key={index} style={{ width: column.width }} />
      ))}
    </colgroup>
  )
}

const SUPPLIERS_SORT_BY_VALUES = [
  'name',
  'packedItemCount',
  'funnelStatus',
  'redFlags',
] as const satisfies readonly PackagingSuppliersOverviewSortBy[]

function parseSupplierSortBy(
  value: string | undefined
): PackagingSuppliersOverviewSortBy | undefined {
  if (value == null || value.trim() === '') {
    return undefined
  }
  if (
    SUPPLIERS_SORT_BY_VALUES.includes(value as PackagingSuppliersOverviewSortBy)
  ) {
    return value as PackagingSuppliersOverviewSortBy
  }
  return undefined
}

function parseSupplierSortDir(
  value: string | undefined
): PackagingSuppliersOverviewSortDir {
  return value === 'desc' ? 'desc' : 'asc'
}

type PackagingSuppliersTableProps = {
  initialListState: SuppliersListState
}

function buildColumnFiltersFromListState(
  state: SuppliersListState
): ColumnFiltersState {
  const filters: ColumnFiltersState = []
  if (state.funnel !== undefined && state.funnel.length > 0) {
    filters.push({ id: 'funnelStatus', value: state.funnel })
  }
  if (state.redFlags !== undefined && state.redFlags.length > 0) {
    filters.push({ id: 'redFlags', value: state.redFlags })
  }
  return filters
}

function isTruncatedSupplierCell(columnId: string): boolean {
  return columnId === 'name'
}

function isWrappableSupplierHeader(columnId: string): boolean {
  return columnId === 'funnelStatus'
}

export function PackagingSuppliersTable({
  initialListState,
}: PackagingSuppliersTableProps) {
  const { t } = useTranslation('packagingSuppliers')
  const { data: organization } = useCurrentOrganization()
  const { data: session } = useGetUserSession()
  const organizationId = session?.organizationId
  const latestListStateRef = useRef<Parameters<typeof saveSuppliersListState>[1] | null>(
    null
  )

  const [searchQuery, setSearchQuery] = useState(
    initialListState.searchQuery ?? ''
  )
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(
    (initialListState.searchQuery ?? '').trim()
  )
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: initialListState.page - 1,
    pageSize: initialListState.pageSize,
  })
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(() =>
    buildColumnFiltersFromListState(initialListState)
  )
  const [sortBy, setSortBy] = useState<PackagingSuppliersOverviewSortBy | undefined>(
    () => parseSupplierSortBy(initialListState.sortBy)
  )
  const [sortDir, setSortDir] = useState<PackagingSuppliersOverviewSortDir>(() =>
    parseSupplierSortDir(initialListState.sortDir)
  )

  const sorting = useMemo<SortingState>(() => {
    if (sortBy == null) {
      return []
    }
    return [{ id: sortBy, desc: sortDir === 'desc' }]
  }, [sortBy, sortDir])

  const onSortingChange: OnChangeFn<SortingState> = (updater) => {
    const next = typeof updater === 'function' ? updater(sorting) : updater
    const primary = next[0]
    if (primary?.id == null || primary.id === '') {
      setSortBy(undefined)
      setSortDir('asc')
      setPagination((previous) => ({ ...previous, pageIndex: 0 }))
      return
    }
    const nextSortBy = primary.id as PackagingSuppliersOverviewSortBy
    const nextSortDir: PackagingSuppliersOverviewSortDir = primary.desc
      ? 'desc'
      : 'asc'
    setSortBy(nextSortBy)
    setSortDir(nextSortDir)
    setPagination((previous) => ({ ...previous, pageIndex: 0 }))
  }

  const onPaginationChange: OnChangeFn<PaginationState> = useCallback(
    (updater) => {
      setPagination((previous) =>
        typeof updater === 'function' ? updater(previous) : updater
      )
    },
    []
  )

  const onColumnFiltersChange: OnChangeFn<ColumnFiltersState> = useCallback(
    (updater) => {
      setColumnFilters((previous) =>
        typeof updater === 'function' ? updater(previous) : updater
      )
      setPagination((previous) => ({ ...previous, pageIndex: 0 }))
    },
    []
  )

  const columns = useMemo<ColumnDef<PackagingSupplier>[]>(
    () => [
      {
        accessorKey: 'name',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t('list.columnName')} />
        ),
        cell: ({ row }) => (
          <PackagingSupplierNameCell
            supplierId={row.original.id}
            name={row.original.name}
            notInSourceList={row.original.notInSourceList ?? false}
          />
        ),
      },
      {
        id: 'packedItemCount',
        accessorFn: (row) => row.packedItemCount ?? 0,
        header: ({ column }) => (
          <DataTableColumnHeader
            column={column}
            title={t('list.columnPackedItems')}
          />
        ),
        cell: ({ row }) => (
          <span className='tabular-nums'>{row.original.packedItemCount ?? 0}</span>
        ),
      },
      {
        id: 'funnelStatus',
        accessorFn: (row) => derivePackagingSupplierFunnelStage(row),
        filterFn: (row, id, value) => {
          const selectedValues = Array.isArray(value) ? value : []
          if (selectedValues.length === 0) {
            return true
          }
          return selectedValues.includes(String(row.getValue(id)))
        },
        header: ({ column }) => (
          <DataTableColumnHeader
            column={column}
            title={t('list.columnFunnelStatus')}
          />
        ),
        cell: ({ row }) => (
          <PackagingSupplierFunnelStatusCell supplier={row.original} />
        ),
      },
      {
        id: 'redFlags',
        accessorFn: (row) => row.redFlags?.length ?? 0,
        getUniqueValues: (row) => row.redFlags ?? [],
        filterFn: 'arrIncludesSome',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t('list.columnAudit')} />
        ),
        cell: ({ row }) => (
          <PackagingSupplierRedFlagsCell
            redFlags={row.original.redFlags ?? []}
          />
        ),
      },
      {
        id: 'notInSourceList',
        accessorFn: (row) => Boolean(row.notInSourceList),
        filterFn: (row, id, value) => {
          if (value !== true) {
            return true
          }
          return row.getValue(id) === true
        },
        enableHiding: true,
      },
    ],
    [t]
  )

  useEffect(() => {
    const handle = window.setTimeout(() => {
      setDebouncedSearchQuery(searchQuery.trim())
    }, 300)
    return () => {
      window.clearTimeout(handle)
    }
  }, [searchQuery])

  useEffect(() => {
    if (!organizationId) {
      return
    }

    const funnelFilter = columnFilters.find(
      (filter) => filter.id === 'funnelStatus'
    )
    const redFlagsFilter = columnFilters.find(
      (filter) => filter.id === 'redFlags'
    )

    const nextState = {
      page: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
      funnel:
        Array.isArray(funnelFilter?.value) && funnelFilter.value.length > 0
          ? funnelFilter.value.map(String)
          : undefined,
      redFlags:
        Array.isArray(redFlagsFilter?.value) &&
        redFlagsFilter.value.length > 0
          ? redFlagsFilter.value.map(String)
          : undefined,
      searchQuery: debouncedSearchQuery.length > 0 ? debouncedSearchQuery : undefined,
      sortBy,
      sortDir: sortBy == null ? undefined : sortDir,
    }

    latestListStateRef.current = nextState
    saveSuppliersListState(organizationId, nextState)
  }, [
    columnFilters,
    debouncedSearchQuery,
    organizationId,
    pagination.pageIndex,
    pagination.pageSize,
    sortBy,
    sortDir,
  ])

  useEffect(() => {
    return () => {
      if (!organizationId || latestListStateRef.current === null) {
        return
      }
      saveSuppliersListState(organizationId, latestListStateRef.current)
    }
  }, [organizationId])

  const listQuery = useMemo(() => {
    const funnelFilter = columnFilters.find(
      (filter) => filter.id === 'funnelStatus'
    )
    const redFlagsFilter = columnFilters.find(
      (filter) => filter.id === 'redFlags'
    )
    const notInSourceListOnly = columnFilters.some(
      (filter) => filter.id === 'notInSourceList' && filter.value === true
    )

    return {
      page: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
      searchQuery:
        debouncedSearchQuery.length > 0 ? debouncedSearchQuery : undefined,
      funnel:
        Array.isArray(funnelFilter?.value) && funnelFilter.value.length > 0
          ? funnelFilter.value.map(String)
          : undefined,
      redFlags:
        Array.isArray(redFlagsFilter?.value) &&
        redFlagsFilter.value.length > 0
          ? (redFlagsFilter.value.map(String) as PackagingSupplier['redFlags'])
          : undefined,
      notInSourceList: notInSourceListOnly ? true : undefined,
      sortBy,
      sortDir,
    }
  }, [columnFilters, debouncedSearchQuery, pagination.pageIndex, pagination.pageSize, sortBy, sortDir])

  const exportQuery = useMemo(() => {
    const { page: _page, pageSize: _pageSize, ...filters } = listQuery
    return filters
  }, [listQuery])

  const { data: suppliersList, isLoading, isFetching } =
    useListPackagingSuppliers(listQuery)
  const data = suppliersList?.suppliers ?? []
  const pageInfo = suppliersList?.pageInfo
  const partsCatalogConfigured = suppliersList?.partsCatalogConfigured ?? false

  const notInSourceListOnly = columnFilters.some(
    (filter) => filter.id === 'notInSourceList' && filter.value === true
  )

  const setNotInSourceListOnly = (checked: boolean) => {
    onColumnFiltersChange((previous) => {
      const withoutBomFilter = previous.filter(
        (filter) => filter.id !== 'notInSourceList'
      )
      if (!checked) {
        return withoutBomFilter
      }
      return [...withoutBomFilter, { id: 'notInSourceList', value: true }]
    })
  }

  const toolbarFilters = useMemo(
    () => [
      {
        columnId: 'funnelStatus',
        title: t('list.columnFunnelStatus'),
        options: PACKAGING_SUPPLIER_FUNNEL_STAGE_VALUES.map((value) => ({
          value,
          label: t(funnelStageLabelKey(value)),
        })),
      },
      {
        columnId: 'redFlags',
        title: t('list.columnAudit'),
        options: PACKAGING_FIELD_CROSS_CHECK_ISSUE_CODE_VALUES.map((value) => ({
          value,
          label: t(auditIssueLabelKey(value)),
        })),
      },
    ],
    [t]
  )

  const filteredData = data

  const table = useReactTable({
    data: filteredData,
    columns,
    state: {
      globalFilter: searchQuery,
      columnFilters,
      columnVisibility: { notInSourceList: false },
      pagination,
      sorting,
    },
    onGlobalFilterChange: setSearchQuery,
    onColumnFiltersChange,
    onPaginationChange,
    onSortingChange,
    manualPagination: true,
    manualFiltering: true,
    manualSorting: true,
    pageCount: pageInfo?.totalPages ?? 0,
    getCoreRowModel: getCoreRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
  })

  useEffect(() => {
    const totalPages = pageInfo?.totalPages ?? 0
    if (totalPages > 0 && pagination.pageIndex >= totalPages) {
      setPagination((previous) => ({
        ...previous,
        pageIndex: totalPages - 1,
      }))
    }
  }, [pageInfo?.totalPages, pagination.pageIndex])

  const showNotInSourceListFilter = partsCatalogConfigured
  const hasActiveFilters =
    columnFilters.length > 0 || Boolean(table.getState().globalFilter)

  const exportFileName = useMemo(
    () =>
      buildPackagingExportFilename({
        date: new Date(),
        organizationName: organization?.name,
        kind: 'suppliers',
      }),
    [organization?.name]
  )

  return (
    <div
      className={cn(
        'max-sm:has-[div[role="toolbar"]]:mb-16',
        'flex min-h-0 flex-1 flex-col gap-4'
      )}
    >
      <div className='flex shrink-0 flex-col gap-3 sm:flex-row sm:items-start sm:justify-between'>
        <div className='min-w-0 flex-1'>
          <DataTableToolbar
            table={table}
            searchPlaceholder={t('searchPlaceholder')}
            filters={toolbarFilters}
            showFilterCounts={false}
            showViewOptions={false}
            actions={
              <ExportXlsxButton
                download={async () => {
                  await downloadPackagingSuppliersExport(
                    exportQuery,
                    exportFileName
                  )
                }}
                hasActiveFilters={hasActiveFilters}
              />
            }
          />
        </div>

        {showNotInSourceListFilter && (
          <div className='flex items-center gap-2 sm:pt-1'>
            <Switch
              id='suppliers-not-in-source-list'
              checked={notInSourceListOnly}
              onCheckedChange={setNotInSourceListOnly}
            />
            <Label htmlFor='suppliers-not-in-source-list'>
              {t('list.notInSourceListFilter')}
            </Label>
          </div>
        )}
      </div>
      <div
        className='flex min-h-0 flex-1 flex-col overflow-hidden rounded-md border'
        aria-busy={isFetching || isLoading}
      >
        <div className='min-h-0 flex-1 overflow-auto'>
          <table className='w-full min-w-full table-fixed caption-bottom text-sm'>
            <SuppliersTableColGroup />
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id} className='group/row'>
                  {headerGroup.headers.map((header) => (
                    <TableHead
                      key={header.id}
                      colSpan={header.colSpan}
                      className={
                        isWrappableSupplierHeader(header.column.id)
                          ? 'sticky top-0 z-10 bg-background whitespace-normal group-hover/row:bg-muted'
                          : 'sticky top-0 z-10 bg-background group-hover/row:bg-muted'
                      }
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows.length > 0 ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow key={row.id} className='group/row'>
                    {row.getVisibleCells().map((cell) => (
                      <TableCell
                        key={cell.id}
                        className={
                          isTruncatedSupplierCell(cell.column.id)
                            ? 'max-w-0 truncate overflow-hidden bg-background group-hover/row:bg-muted'
                            : 'bg-background group-hover/row:bg-muted'
                        }
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className='h-24 text-center text-muted-foreground'
                  >
                    {t('notFound')}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </table>
        </div>
        <DataTablePagination
          table={table}
          className='shrink-0 border-t bg-background py-3'
        />
      </div>
    </div>
  )
}
